import { formatMoney, formatPercent } from "@justhome/common";
import { OfferPreview } from "@justhome/api-core";
import clsx from "clsx";
import { FC, MouseEventHandler } from "react";
import Badge from "../Badge";
import Button from "../Button";
import { formatDate, formatYearMonths } from "../lib/formatting";
import { Locale } from "../lib/i18n";
import { offerLabels } from "../lib/labels";
import OfferCombinationInfo from "../Offer/OfferCombinationInfo";
import OfferText from "../Offer/OfferText";
import OfferLegalNotice from "../OfferLegalNotice";
import Skeleton from "../Skeleton";
import OfferBody from "./OfferBody";

export type OfferCardMode = "interestRate" | "installment";
export type OfferCardLayout = "wide" | "default";

type OfferCardProps = {
  offer?: OfferPreview;
  className?: string;
  isLowestMonthlyInstallment?: boolean;
  isLowestInterestRate?: boolean;
  onButtonClick?: MouseEventHandler;
  buttonLabel?: string;
  href?: string;
  locale?: Locale;
  isLoading?: boolean;
  showResidualDebt?: boolean;
  showTotalInterest?: boolean;
  addBadgePlaceholder?: boolean;
  showExpirationDate?: boolean;
};

const OfferCard: FC<OfferCardProps> = ({
  offer,
  className,
  isLowestMonthlyInstallment,
  isLowestInterestRate,
  onButtonClick,
  buttonLabel,
  href,
  locale = "de",
  isLoading,
  addBadgePlaceholder,
  showExpirationDate,
  showResidualDebt,
}) => {
  const interestRate = offer?.interestRate;
  const effectiveInterestRate = offer?.effectiveInterestRate;
  const loans = offer?.loans;
  const loanDurationInMonths = offer?.loanDurationInMonths;
  const labels = offerLabels[locale];
  const repaymentRate = loans && loans[0]?.initialRepaymentRate;
  const expirationDate =
    offer?.expirationDate && new Date(offer?.expirationDate);
  const residualDebt = offer?.residualDebtAfterFixedInterestPeriod;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isExpired = expirationDate && expirationDate < today;

  return (
    <div
      data-testid="offer"
      className={clsx(
        "relative flex flex-col border rounded-s border-neutral-50 bg-neutral-0 p-s pt-2xs pb-2xs",
        className,
      )}
    >
      {showExpirationDate && expirationDate && (
        <Badge color={isExpired ? "error" : "primary"}>
          {`${labels.expirationDate} ${formatDate(expirationDate, locale)}`}
        </Badge>
      )}
      <OfferBody
        isLoading={isLoading}
        locale={locale}
        offer={offer}
        isLowestMonthlyInstallment={isLowestMonthlyInstallment}
        isLowestInterestRate={isLowestInterestRate}
        addBadgePlaceholder={addBadgePlaceholder}
      />
      <div className="l:flex gap-3s items-end">
        <div className="flex-1 w-full flex flex-col gap-x-m gap-y-2xs">
          <div className="flex flex-col grow gap-y-xs gap-x-m">
            {isLoading && (
              <>
                <Skeleton
                  isLoading={isLoading}
                  width="100%"
                  height={20}
                ></Skeleton>
                <Skeleton
                  isLoading={isLoading}
                  width="100%"
                  height={20}
                ></Skeleton>
              </>
            )}

            {!isLoading && (
              <>
                <OfferText
                  text={labels.interestRate}
                  value={formatPercent(interestRate || 0, locale)}
                  color={
                    isLowestInterestRate && isLowestMonthlyInstallment
                      ? "purple"
                      : isLowestInterestRate
                        ? "info"
                        : undefined
                  }
                />
                {!!repaymentRate && (
                  <OfferText
                    text={labels.repaymentRate}
                    value={formatPercent(repaymentRate, locale)}
                  />
                )}
                {!!effectiveInterestRate && (
                  <OfferText
                    text={labels.effectiveInterestRate}
                    value={formatPercent(effectiveInterestRate || 0, locale)}
                  />
                )}
                {!!loanDurationInMonths && (
                  <OfferText
                    text={labels.loanDuration}
                    value={formatYearMonths(loanDurationInMonths, locale)}
                    info={labels.loanDurationInfo}
                  />
                )}
                {!!residualDebt && showResidualDebt && (
                  <OfferText
                    text={labels.residualDebt}
                    value={formatMoney(residualDebt, false, locale)}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {!isLoading && offer?.combined && (
        <OfferCombinationInfo
          offer={offer}
          locale={locale}
          className="mt-xs mx-auto"
        />
      )}
      <div className="mt-auto pt-3xs">
        {buttonLabel && (
          <Skeleton isLoading={isLoading} height={36}>
            <Button
              size="sm"
              className="!w-full"
              onClick={onButtonClick}
              iconEnd="RightSmall"
              href={href}
              target="_blank"
              data-testid="offer-card-button"
            >
              {buttonLabel}
            </Button>
          </Skeleton>
        )}
      </div>
      <div className="pt-2xs flex justify-end">
        <OfferLegalNotice offer={offer} isLoading={isLoading} locale={locale} />
      </div>
    </div>
  );
};

export default OfferCard;
